import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Tag } from '@forwardflow/ui-v2'
import { STEP_LABELS } from '@forwardflow/constants/onboarding.constants'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import {
   DEV_DUMMY,
   FREE_FLOW_ORG_ID,
   homePath,
   isSandbox,
   isDevToolsEnabled,
   isFreeFlow,
   requestDevTools,
   setFreeFlow,
} from './devTools'

const HIDE_KEY = 'ff-onboarding:devtools-hidden'

/**
 * Hidden testing bar for the onboarding wizard. See `devTools.ts` for when
 * it shows. Rendered by `OnboardingLayout`; renders nothing in production.
 */
export const OnboardingDevBar = () => {
   const navigate = useNavigate()
   const [enabled] = useState(isDevToolsEnabled)
   const [freeFlow, setFreeFlowState] = useState(isFreeFlow)
   const [hidden, setHidden] = useState(
      () => sessionStorage.getItem(HIDE_KEY) === '1'
   )
   const {
      onboardingData,
      isLoadingOrInitialization,
      adoptEmptyRecordFor,
      getNextStep,
      goToStep,
      setIsFUBConnected,
      setIsSubscriptionActivated,
      setCampaignConfiguration,
      setIsOnBoardingCompleted,
   } = useOnboardingStoreV2()

   // Free flow works on a detached sandbox record that is never saved. Adopt
   // it once the real record has finished loading (or failed to).
   useEffect(() => {
      if (!enabled || !freeFlow || isLoadingOrInitialization) return
      if (onboardingData.organizationId !== FREE_FLOW_ORG_ID) {
         adoptEmptyRecordFor(FREE_FLOW_ORG_ID, 'fub-connect')
      }
   }, [
      enabled,
      freeFlow,
      isLoadingOrInitialization,
      onboardingData.organizationId,
      adoptEmptyRecordFor,
   ])

   if (!enabled || hidden) return null

   const step = onboardingData.currentStep

   /** Store-only skip, used when the step did not claim the request. */
   const skipWithStore = () => {
      switch (step) {
         case 'fub-connect':
            setIsFUBConnected(true)
            getNextStep()
            break
         case 'payment-details':
         case 'budget-and-card':
         case 'payment':
            setIsSubscriptionActivated(true)
            getNextStep()
            break
         case 'phone-number-configuration':
         case 'number-and-stops':
         case 'area-code':
            if (!onboardingData.campaignConfiguration?.area_codes?.length) {
               setCampaignConfiguration({ area_codes: DEV_DUMMY.areaCodes.slice(0, 1) })
            }
            getNextStep()
            break
         case 'stage-mapping-explained':
         case 'start-calling':
         case 'business-details':
            setIsOnBoardingCompleted(true)
            navigate(homePath())
            break
         default:
            getNextStep()
      }
   }

   const skip = () => {
      if (!requestDevTools('skip')) skipWithStore()
   }

   const toggleFreeFlow = () => {
      const next = !freeFlow
      setFreeFlow(next)
      setFreeFlowState(next)
      // Off: reload so the real record is read again. On: the effect above
      // adopts the sandbox.
      if (!next) window.location.reload()
   }

   const hide = () => {
      sessionStorage.setItem(HIDE_KEY, '1')
      setHidden(true)
   }

   return (
      <div
         data-testid="onboarding-dev-bar"
         className="fixed top-3 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full border border-[#FF8904]/40 bg-[#0A0A12]/90 backdrop-blur px-3 py-1.5 shadow-lg"
      >
         <Tag color="warning">DEV</Tag>
         <span className="text-xs text-white/70 pr-1">
            {STEP_LABELS[step] ?? step}
         </span>
         {!isSandbox() && (
         <button
            type="button"
            onClick={toggleFreeFlow}
            title="Free flow: every step continues with no input and nothing is saved"
            className={`rounded-full px-3 h-7 text-xs border transition-colors ${
               freeFlow
                  ? 'border-[#00D492]/60 bg-[#00D492]/15 text-[#A4F4CF]'
                  : 'border-white/15 bg-white/5 text-white/60'
            }`}
         >
            Free flow {freeFlow ? 'on' : 'off'}
         </button>
         )}
         <Button
            size="small"
            variant="secondary"
            className="!h-7 !px-3 !text-xs"
            onClick={() => requestDevTools('fill')}
         >
            Fill this step
         </Button>
         <Button
            size="small"
            variant="secondary"
            className="!h-7 !px-3 !text-xs"
            onClick={skip}
         >
            Skip this step
         </Button>
         <Button
            size="small"
            variant="secondary"
            className="!h-7 !px-3 !text-xs"
            onClick={() => goToStep('fub-connect')}
         >
            Restart
         </Button>
         <button
            type="button"
            aria-label="Hide dev bar"
            className="text-white/50 hover:text-white text-xs px-1"
            onClick={hide}
         >
            ×
         </button>
      </div>
   )
}
